import React, { useState } from 'react';
import {
  BriefcaseBusinessIcon,
  FolderIcons,
  FolderPlusIcons,
  PencilIcons,
  TrashIcons,
  UsersIcons,
} from '../../../../icons/icons';
import { CandiBankFolder } from '../../../../utils/types/candi_bank_types';
import { CandiBankFoldersState } from '../../hooks/useCandiBankFolders';
import { isCandidateDrag, readCandidateDrag } from '../../utils/folderDrag';

interface FolderSidebarProps {
  state: CandiBankFoldersState;
  /** Total people in the bank, shown on "All candidates". */
  totalCandidates?: number;
  onNewFolder: () => void;
  onEditFolder: (folder: CandiBankFolder) => void;
  onDeleteFolder: (folder: CandiBankFolder) => void;
}

/**
 * "All candidates" plus every folder in the org, grouped under its creator's name. Each folder
 * is a drop target for candidate rows and cards.
 */
export const FolderSidebar: React.FC<FolderSidebarProps> = ({
  state,
  totalCandidates,
  onNewFolder,
  onEditFolder,
  onDeleteFolder,
}) => {
  const { groups, activeFolderId, setActiveFolderId, addCandidates, loading, folders } = state;
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const dropHandlers = (folder: CandiBankFolder) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!isCandidateDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (dropTarget !== folder.id) setDropTarget(folder.id);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropTarget(null);
      addCandidates(folder, readCandidateDrag(e));
    },
  });

  const rowClass = (active: boolean, dropping: boolean) =>
    `group w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] transition-colors ${
      dropping
        ? 'bg-indigo-100 ring-2 ring-indigo-400 text-indigo-800'
        : active
          ? 'bg-indigo-50 text-indigo-700 font-bold'
          : 'text-slate-600 hover:bg-slate-100 font-medium'
    }`;

  return (
    <aside className='w-60 shrink-0 bg-white border border-slate-200 rounded-lg p-2 self-start sticky top-4'>
      <button
          type='button'
          onClick={() => setActiveFolderId(null)}
          className={rowClass(activeFolderId === null, false)}
      >
        <UsersIcons className='w-3.5 h-3.5 shrink-0' />
        <span className='flex-1 text-left truncate'>All candidates</span>
        { totalCandidates !== undefined && (
          <span className='text-[11px] text-slate-400 font-medium'>{ totalCandidates }</span>
        ) }
      </button>

      <div className='flex items-center justify-between mt-3 mb-1 px-2.5'>
        <span className='text-[10px] font-bold text-slate-400 uppercase tracking-wider'>Folders</span>
        <button
            type='button'
            onClick={onNewFolder}
            title='New folder'
            aria-label='New folder'
            className='p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'
        >
          <FolderPlusIcons className='w-3.5 h-3.5' />
        </button>
      </div>

      { !loading && folders.length === 0 && (
        <p className='px-2.5 py-2 text-[11px] text-slate-400 leading-relaxed'>
          No folders yet. Create one, then drag candidates onto it.
        </p>
      ) }

      <div className='max-h-[calc(100vh-260px)] overflow-y-auto'>
        { groups.map((group) => (
          <div key={group.creatorId} className='mb-2'>
            <div className='px-2.5 pt-1.5 pb-1 text-[11px] font-semibold text-slate-500 truncate'>
              { group.creatorName }
            </div>
            { group.folders.map((folder) => (
              <div
                  key={folder.id}
                  {...dropHandlers(folder)}
                  className={rowClass(activeFolderId === folder.id, dropTarget === folder.id)}
              >
                <button
                    type='button'
                    onClick={() => setActiveFolderId(folder.id)}
                    className='flex-1 min-w-0 flex items-center gap-2 text-left'
                    title={folder.jobOpeningTitle ? `${folder.name} · ${folder.jobOpeningTitle}` : folder.name}
                >
                  <FolderIcons className='w-3.5 h-3.5 shrink-0' />
                  <span className='truncate'>{ folder.name }</span>
                  { folder.jobOpeningId && (
                    <BriefcaseBusinessIcon className='w-3 h-3 shrink-0 text-slate-400' />
                  ) }
                </button>
                <span className='text-[11px] text-slate-400 font-medium group-hover:hidden group-focus-within:hidden'>
                  { folder.candidateCount }
                </span>
                <span className='hidden group-hover:flex group-focus-within:flex items-center gap-0.5'>
                  <button
                      type='button'
                      onClick={() => onEditFolder(folder)}
                      title='Edit folder'
                      aria-label={`Edit ${folder.name}`}
                      className='p-0.5 rounded text-slate-400 hover:text-indigo-600'
                  >
                    <PencilIcons className='w-3 h-3' />
                  </button>
                  { folder.canDelete && (
                    <button
                        type='button'
                        onClick={() => onDeleteFolder(folder)}
                        title='Delete folder'
                        aria-label={`Delete ${folder.name}`}
                        className='p-0.5 rounded text-slate-400 hover:text-red-600'
                    >
                      <TrashIcons className='w-3 h-3' />
                    </button>
                  ) }
                </span>
              </div>
            )) }
          </div>
        )) }
      </div>
    </aside>
  );
};
